import React from "react";
import { MODULE_SCOPES } from "../../../constants/moduleScopes";
import { getRoleDisplayName } from "../../../utils/roleUtils";

/**
 * Generated-invite-link success panel: confirmation header, copy-to-clipboard
 * box, and the link's configuration summary (domain, role, per-module scope).
 * Presentational — the container owns `linkPreview`/`linkCopied` state and the
 * copy handler.
 *
 * @param {object} props
 * @param {{ url: string, domain: string|null, role: string, productScopes: Record<string,string[]> }} props.linkPreview
 * @param {boolean} props.linkCopied
 * @param {() => void} props.onCopy
 */
export default function LinkPreview({ linkPreview, linkCopied, onCopy }) {
  return (
    <div className="flex flex-col gap-5">
      {/* Success header */}
      <div className="flex flex-col items-center gap-2 py-4">
        <div className="w-12 h-12 bg-green-50 border border-green-200 rounded-full flex items-center justify-center">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        </div>
        <div className="text-center">
          <p className="text-gray-900 text-base font-semibold font-['Inter']">Invite link generated</p>
          <p className="text-gray-500 text-xs font-['Inter'] mt-0.5">Share this link with your team. Anyone with the link can register.</p>
        </div>
      </div>

      {/* Link copy box */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
        <span className="flex-1 text-sm text-gray-700 font-['Inter'] truncate">{linkPreview.url}</span>
        <button
          onClick={onCopy}
          className={`shrink-0 flex items-center gap-1.5 px-3 h-7 rounded-md text-xs font-medium font-['Inter'] transition-all duration-200 ${
            linkCopied
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 cursor-pointer"
          }`}
        >
          {linkCopied ? (
            <>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              Copy
            </>
          )}
        </button>
      </div>

      {/* Config summary */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide font-['Inter']">Link configuration</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-0.5 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <span className="text-xs text-gray-400 font-['Inter']">Email domain</span>
            <span className="text-sm font-medium text-gray-800 font-['Inter']">
              {linkPreview.domain ? `@${linkPreview.domain}` : "Any domain"}
            </span>
          </div>
          <div className="flex flex-col gap-0.5 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <span className="text-xs text-gray-400 font-['Inter']">Role</span>
            <span className="text-sm font-medium text-gray-800 font-['Inter']">
              {getRoleDisplayName(linkPreview.role)}
            </span>
          </div>
          {linkPreview.role !== "super_admin" &&
            MODULE_SCOPES.map((module) => {
              const markets = linkPreview.productScopes?.[module.key] || [];
              return (
                <div
                  key={module.key}
                  className="flex flex-col gap-0.5 p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <span className="text-xs text-gray-400 font-['Inter']">
                    {module.label}
                  </span>
                  <span className="text-sm font-medium text-gray-800 font-['Inter']">
                    {markets.length > 0
                      ? markets.map((m) => m.toUpperCase()).join(", ")
                      : "—"}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
